import { useEffect, useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useLocalAuth } from '@/auth/LocalAuthContext'
import { useCommandCenterLoans } from '@/hooks/useDemoDataAccess'
import { useLiveTriggerAlerts } from '@/hooks/useLiveTriggerAlerts'
import api from '@/services/api'
import { useNotificationStore } from '@/store/notificationStore'
import { triggerNotification } from '@/utils/triggerAlerts'

/**
 * Feeds the notification bell from a live source: open triggers on the active
 * book, the same set Command Center's Alert Feed shows. Honours the platform's
 * "Trigger alerts" switch when the reader can read platform settings; a reader
 * who cannot gets the platform default (on). Renders nothing.
 */
export default function LiveNotificationSync() {
  const { localUser } = useLocalAuth()
  const actions = localUser?.permissions?.['asset-management']
  const roleMaySee = Boolean(localUser?.is_super_admin) || !actions || actions.includes('view')
  const loans = useCommandCenterLoans(roleMaySee)
  const { alerts, available } = useLiveTriggerAlerts(loans)
  const syncLive = useNotificationStore((state) => state.syncLive)

  const settings = useQuery({
    queryKey: ['settings', 'platform', 'notifications'],
    queryFn: async () =>
      (await api.get<{ data?: { notifications?: { triggerAlerts?: boolean } } }>('/settings/platform'))
        .data,
    enabled: roleMaySee,
    retry: false,
    staleTime: 300_000,
  })
  const triggerAlertsOn = settings.data?.data?.notifications?.triggerAlerts !== false

  const items = useMemo(
    () => (available && triggerAlertsOn ? alerts.map((alert) => triggerNotification(alert)) : []),
    [alerts, available, triggerAlertsOn],
  )

  useEffect(() => {
    syncLive('trigger', items)
  }, [items, syncLive])

  return null
}
